import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApi } from '@/api/useApi';
import type { CaseSummary } from '@/api/types';
import {
  Card,
  ErrorState,
  Loading,
  Page,
  PageHead,
  Tag,
  Segmented,
  Table,
} from '@/components/ui';
import { formatRelative, humanise } from '@/lib/format';

const FILTERS = [
  { value: 'open', label: 'Open', query: '?state=open' },
  { value: 'urgent', label: 'Urgent', query: '?state=open&type=urgent_concern' },
  { value: 'unassigned', label: 'Unassigned', query: '?state=open&unassigned=true' },
  { value: 'resolved', label: 'Resolved', query: '?state=resolved' },
  // Explicit, not empty: an absent state falls back to the endpoint's
  // "open" default, which made this tab hide the resolved cases.
  { value: 'all', label: 'All', query: '?state=all' },
] as const;

type FilterKey = (typeof FILTERS)[number]['value'];

export function slaTone(state: CaseSummary['sla_state']) {
  if (state === 'breached') return { label: 'Past target', tone: 'urgent' as const };
  if (state === 'near_target') return { label: 'Nearing target', tone: 'attention' as const };
  if (state === 'on_track') return { label: 'Within target', tone: 'positive' as const };
  return { label: 'No target set', tone: 'neutral' as const };
}

/** ADM-04. Triage, ownership, and the agency's own SLA clock in one queue. */
export function FeedbackPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterKey>('open');
  const query = FILTERS.find((entry) => entry.value === filter)!.query;
  const { data, error, loading, reload } = useApi<{ cases: CaseSummary[] }>(
    `/admin/feedback${query}`,
  );

  if (loading) return <Loading />;
  if (error) return <Page><ErrorState message={error.message} onRetry={reload} /></Page>;

  const cases = data?.cases ?? [];

  return (
    <Page>
      <PageHead
        title="Feedback"
        subtitle="Triage and response"
        actions={
          <Segmented
            label="Filter cases"
            options={FILTERS.map((entry) => ({ value: entry.value, label: entry.label }))}
            value={filter}
            onChange={setFilter}
          />
        }
      />

      <div className="stack">
        <Table
          columns={['Subject', 'Type', 'Client', 'Owner', 'State', 'Target', 'Received']}
          empty="Nothing in this queue."
        >
          {cases.map((item) => {
            const sla = slaTone(item.sla_state);
            return (
              <tr key={item.id} data-clickable="true" onClick={() => navigate(`/feedback/${item.id}`)}>
                <td className="strong">{item.subject}</td>
                <td>
                  <Tag
                    label={humanise(item.type)}
                    tone={
                      item.type === 'urgent_concern'
                        ? 'urgent'
                        : item.type === 'concern'
                          ? 'attention'
                          : 'info'
                    }
                  />
                </td>
                <td>{item.recipient_name ?? <span className="meta">—</span>}</td>
                <td>{item.owner_name ?? <span className="meta">Unassigned</span>}</td>
                <td>
                  <Tag
                    label={humanise(item.state)}
                    tone={item.state === 'resolved' ? 'positive' : 'attention'}
                  />
                </td>
                <td>
                  <Tag label={sla.label} tone={sla.tone} />
                </td>
                <td className="meta">{formatRelative(item.created_at)}</td>
              </tr>
            );
          })}
        </Table>

        <Card>
          <p className="meta">
            Response targets come from your agency&apos;s configured policy. Where no target is
            set, the product does not invent one — it says so instead.
          </p>
        </Card>
      </div>
    </Page>
  );
}
